import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { analyticsProps } from '@class-ui/insights';
import { LockOpenOutlined, LockOutlined } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    type DialogProps,
    Stack,
    styled,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import { SubClassContainer } from './SubClassContainer';
import { SubClassesCreateDialog } from './SubClassesCreateDialog';
import { SubClassesDeleteDialog } from './SubClassesDeleteDialog';
import { CREATE_LTI_CODE } from '../../../config/apiPaths';
import { ClassContext } from '../../../context/ClassContext';
import { type ClassRecordFromApi, Roles, type SubClassRecord } from '../../../context/types';
import { getUserUuidHeaders } from '../../../utils/httpUtils';
import { getUrlParams, getUrls } from '../../../utils/url';
import { StyledActionButton } from '../../../views/AdminSettings/styles/table-styles';
import MainLayout, { type NavControlItem } from '../../layout/MainLayout';
import { NoRecords } from '../SharedComponents';

const ActionsBox = styled(Stack)`
    flex-direction: row;
    align-items: center;
    gap: 4px;
`;

const EnterButton = styled(Button)`
    width: 100%;
    display: flex;
    justify-content: flex-start;
    text-underline-offset: 4px;
    ${(props) => (props.disabled ? 'text-decoration: none;' : 'text-decoration: underline;')}

    & .MuiSvgIcon-root {
        margin-left: 8px;
        font-size: 20px;
        color: ${({ theme }) => theme.palette.action.active};
    }
`;

const TableCellSortable = styled(TableCell)`
    cursor: pointer;
`;

interface ClassRowProps {
    currentClassUuid: string;
    subClass: Partial<SubClassRecord>;
    isMain?: boolean;
    showHost?: boolean;
    isPrivilegedUser?: boolean;
    disableEdit?: boolean;
    onClickOpen: (classroom: Partial<ClassRecordFromApi>) => void;
    onClickDelete: (classroom: Partial<ClassRecordFromApi>) => void;
    onClickClose: (event: any) => void;
    onClickLock: (classroom: Partial<ClassRecordFromApi>) => void;
    opening: string;
    isLoading: boolean;
    key: string;
}

enum SortCriteria {
    Name = 'name',
    Host = 'host'
}

type SortOrder = 'asc' | 'desc';

const ClassRow = (props: ClassRowProps) => {
    const {
        currentClassUuid,
        isMain,
        showHost,
        subClass,
        opening,
        isPrivilegedUser,
        disableEdit,
        onClickOpen,
        onClickDelete,
        onClickClose,
        onClickLock,
        isLoading
    } = props;
    const { t } = useTranslation('class');
    const roomName = isMain ? t('sub-class.main-room') : subClass.class_name;
    const subClassUuid = subClass.class_uuid;
    const isCurrent = subClassUuid === currentClassUuid;
    const isLocked = subClass.data?.locked;

    return (
        <TableRow key={`subclass-table-row-${subClassUuid}`}>
            <TableCell key={`subclass-cell-name-${subClassUuid}`}>
                <EnterButton
                    size="medium"
                    variant="text"
                    disabled={!!opening || isLoading}
                    onClick={(event: any) =>
                        subClassUuid === currentClassUuid ? onClickClose(event) : onClickOpen(subClass)
                    }
                >
                    {opening === subClassUuid ? (
                        <>
                            <CircularProgress size={16} style={{ marginLeft: '0.3rem', marginRight: '0.5rem' }} />
                            <Typography>{t('sub-class.opening')}</Typography>
                        </>
                    ) : (
                        <>
                            <Typography>{roomName}</Typography>
                            {isMain && (isLocked ? <LockOutlined /> : <LockOpenOutlined />)}
                        </>
                    )}
                </EnterButton>
            </TableCell>
            {showHost && (
                <TableCell key={`subclass-cell-host-${subClassUuid}`}>
                    <Typography
                        variant="body2"
                        color={subClass.leadInstructor?.userName ? 'text.primary' : 'text.secondary'}
                    >
                        {subClass.leadInstructor?.userName || t('sub-class.no-host')}
                    </Typography>
                </TableCell>
            )}
            {isPrivilegedUser && !disableEdit && (
                <TableCell key={`subclass-cell-actions-${subClassUuid}`}>
                    <ActionsBox>
                        {!isMain ? (
                            <Tooltip title={isCurrent ? t('sub-class.delete-self-unavailable') : ''}>
                                <Box>
                                    <StyledActionButton
                                        color="error"
                                        variant="outlined"
                                        disabled={isCurrent || isLoading}
                                        onClick={() => onClickDelete(subClass)}
                                        key={`subclass-button-delete-${subClassUuid}`}
                                        {...(analyticsProps('subclass-button-delete'), { name: roomName })}
                                    >
                                        {t('delete')}
                                    </StyledActionButton>
                                </Box>
                            </Tooltip>
                        ) : (
                            <Tooltip title={isLocked ? t('unlock-tooltip') : t('lock-tooltip')}>
                                <Box>
                                    <StyledActionButton
                                        variant="outlined"
                                        disabled={isLoading}
                                        onClick={() => onClickLock(subClass)}
                                        key={`subclass-button-lock-${subClassUuid}`}
                                        {...(analyticsProps('subclass-button-lock'), { name: roomName })}
                                    >
                                        {isLocked ? t('unlock') : t('lock')}
                                    </StyledActionButton>
                                </Box>
                            </Tooltip>
                        )}
                    </ActionsBox>
                </TableCell>
            )}
        </TableRow>
    );
};

interface SubClassesHubProps extends DialogProps {
    disableMainClass: boolean;
    disableEdit: boolean;
}

export const SubClassesHub = (props: SubClassesHubProps) => {
    const { onClose, disableMainClass, disableEdit } = props;
    const { t } = useTranslation('class');
    const { subClasses, mainClass, fetchSubClasses, lockClassToggle } = useContainer(SubClassContainer);
    const { me, userUuid, classUuid } = useContext(ClassContext);
    const { api } = getUrls();
    const { enqueueSnackbar } = useSnackbar();
    const isInstructor = me.role === Roles.Teacher;
    const isAssistant = me.role === Roles.Assistant;
    const isPrivilegedUser = isInstructor || isAssistant;
    const showHost = Boolean(mainClass?.leadInstructor);
    const hideMainClass = !mainClass || disableMainClass || (!isPrivilegedUser && mainClass.data?.locked);

    const [createSubClassOpen, setCreateSubClassOpen] = useState(false);
    const [deleteConfirmationClass, setDeleteConfirmationClass] = useState<Partial<SubClassRecord> | null>(null);
    const [opening, setOpening] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { from_scheduler: fromScheduler } = getUrlParams();
    const [sortCriteria, setSortCriteria] = useState<SortCriteria>(SortCriteria.Name);
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const { mutate: toggleLock } = useMutation(lockClassToggle, {
        onMutate: () => setIsLoading(true),
        onSettled: () => setIsLoading(false)
    });

    const onClickClose = useCallback(
        (event) => {
            onClose?.(event, 'backdropClick');
        },
        [onClose]
    );
    const handleCloseSubClassCreateDialog = () => {
        setCreateSubClassOpen(false);
    };

    const onClickOpen = async (classroom: Partial<ClassRecordFromApi>) => {
        if (!classroom.class_uuid) {
            console.error('classroom does not have a uuid', classroom);
            return;
        }

        setOpening(classroom.class_uuid);
        try {
            const {
                data: { code }
            } = await axios.post(
                `${api}${CREATE_LTI_CODE}`,
                { student_id: me.student_id },
                { headers: getUserUuidHeaders(userUuid) }
            );

            const url = new URL(window.location.href);
            url.pathname = url.pathname.replace(/[^/]+$/, classroom.class_uuid); // Replace the last segment of the path
            url.searchParams.set('code', code);
            if (!url.searchParams.has('skipHub')) {
                url.searchParams.set('skipHub', 'true');
            }

            window.open(url.toString(), '_self');
        } catch (err) {
            console.log(err);
            const message = (err as any).response?.status === 401 ? t('error-unauthorized') : t('error-message');
            enqueueSnackbar(message, { variant: 'error' });
        }
        setOpening('');
    };

    const onClickDelete = (classroom: Partial<ClassRecordFromApi>) => {
        setDeleteConfirmationClass(classroom);
    };

    const handleCloseSubClassDeleteDialog = () => {
        setDeleteConfirmationClass(null);
    };

    const handleSort = (criteria: SortCriteria) => {
        const isAsc = sortCriteria === criteria && sortOrder === 'asc';
        setSortCriteria(criteria);
        setSortOrder(isAsc ? 'desc' : 'asc');
    };

    const subClassesSorted = useMemo(
        () =>
            subClasses.sort((a, b) => {
                const aValue = sortCriteria === SortCriteria.Name ? a.class_name : a.leadInstructor?.userName || '';
                const bValue = sortCriteria === SortCriteria.Name ? b.class_name : b.leadInstructor?.userName || '';
                if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
                return 0;
            }),
        [subClasses, sortCriteria, sortOrder]
    );

    const controls = useMemo(() => {
        const result: NavControlItem[] = [];
        if (isLoading || !isPrivilegedUser) {
            return result;
        }

        if (!disableEdit) {
            result.push({
                label: t('sub-class.create-button'),
                dataTestId: 'sub-class-hub-button-create',
                onClick: () => setCreateSubClassOpen(true)
            });
        }
        return result;
    }, [disableEdit, isLoading, isPrivilegedUser, t]);

    useEffect(() => {
        fetchSubClasses().then(() => setIsLoading(false));
    }, [fetchSubClasses]);

    return (
        <MainLayout
            title={`${mainClass?.class_name}${t('sub-class.header-detail')}`}
            controls={controls}
            narrowStyle={fromScheduler === '1'}
            loading={isLoading}
        >
            {isPrivilegedUser && !disableEdit && (
                <>
                    <SubClassesCreateDialog open={createSubClassOpen} onClose={handleCloseSubClassCreateDialog} />
                    <SubClassesDeleteDialog
                        onClose={handleCloseSubClassDeleteDialog}
                        deleteConfirmationClass={deleteConfirmationClass}
                    />
                </>
            )}
            <Table>
                <TableHead>
                    <TableRow key="subclass-header-rooms">
                        <TableCellSortable onClick={() => handleSort(SortCriteria.Name)}>
                            {t('name')}
                            {sortCriteria === SortCriteria.Name && (sortOrder === 'asc' ? ' ▲' : ' ▼')}
                        </TableCellSortable>
                        {showHost && (
                            <TableCellSortable onClick={() => handleSort(SortCriteria.Host)}>
                                {t('sub-class.host')}
                                {sortCriteria === SortCriteria.Host && (sortOrder === 'asc' ? ' ▲' : ' ▼')}
                            </TableCellSortable>
                        )}
                        {isPrivilegedUser && !disableEdit && (
                            <TableCell style={{ width: '20%' }}>{t('actions')}</TableCell>
                        )}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {!hideMainClass && mainClass && (
                        <ClassRow
                            currentClassUuid={classUuid}
                            subClass={mainClass}
                            isMain={true}
                            showHost={showHost}
                            opening={opening}
                            isLoading={isLoading}
                            isPrivilegedUser={isPrivilegedUser}
                            disableEdit={disableEdit}
                            onClickOpen={onClickOpen}
                            onClickDelete={onClickDelete}
                            onClickClose={onClickClose}
                            onClickLock={toggleLock}
                            key={`subclass-row-${mainClass.class_uuid}`}
                        />
                    )}
                    {subClassesSorted.map((subClass) => (
                        <ClassRow
                            currentClassUuid={classUuid}
                            subClass={subClass}
                            showHost={showHost}
                            opening={opening}
                            isLoading={isLoading}
                            isPrivilegedUser={isPrivilegedUser}
                            disableEdit={disableEdit}
                            onClickOpen={onClickOpen}
                            onClickDelete={onClickDelete}
                            onClickClose={onClickClose}
                            onClickLock={toggleLock}
                            key={`subclass-row-${subClass.class_uuid}`}
                        />
                    ))}
                </TableBody>
            </Table>
            {subClasses.length === 0 && hideMainClass && <NoRecords message={t('sub-class.no-class-available')} />}
        </MainLayout>
    );
};
